import { ReactNode, useMemo, useRef, useState } from "react";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputSwitch } from "primereact/inputswitch";
import { Message } from "primereact/message";
import { Tooltip } from "primereact/tooltip";
import toast from "react-hot-toast";

import {
  EmptyState,
  PageHeader,
  RowActions,
  StatusBadge,
  TenantGateBanner,
  rowActionsTableStyle,
  useTenantGate,
} from "../../components/ui";
import { useIsFeatureFlagEnabled } from "../../hooks/useIsFeatureFlagEnabled";
import { useTenantScope } from "../../hooks/useTenantScope";
import {
  POINT_RULE_ACTION_LABELS,
  POINT_RULE_KIND_LABELS,
  PointRule,
  canEditPointRuleField,
  formatPointRuleValue,
  isPointRuleComingSoon,
  isPointRuleActionUpcoming,
  isPointRuleActionWithoutAccrual,
} from "../../models/pointRule";
import {
  useGetPointRulesQuery,
  useUpdatePointRuleMutation,
} from "../../store/services/point-rules";
import { useGetEventSubTypesQuery, useGetEventTypesQuery } from "../../store/services/events-types";
import { getApiErrorMessage } from "../../utils/functions/api-errors";
import { BenefitsMetricsRefreshingIndicator } from "../Reporting/components/BenefitsMetricsWidgets";
import { DeactivatePointRuleDialog } from "./components/DeactivatePointRuleDialog";
import { PointRuleDetailDialog } from "./components/PointRuleDetailDialog";
import {
  EMPTY_POINT_RULE_FILTERS,
  PointRuleFilters,
  PointRuleFilterValues,
  filterPointRules,
  hasPointRuleFilters,
} from "./components/PointRuleFilters";
import { PointRuleFormDialog } from "./components/PointRuleFormDialog";
import {
  PAST_CUSTOM_WINDOW_ACTIVATION_MESSAGE,
  buildActiveToggleChanges,
  formatPointRuleEventScope,
  needsNewCustomWindowToActivate,
} from "./components/pointRuleForm";
import { PointsPageCard, PointsPageLoading } from "./components/PointsPageCard";

const PAGE_TITLE = "Reglas de Puntos";
const POINTS_REQUIRED_HINT = "Cargá los puntos antes de activar la regla";
const WITHOUT_ACCRUAL_HINT = "Esta acción todavía no otorga puntos";
const COMING_SOON_EDIT_HINT = "Próximamente: todavía no se puede configurar";
/**
 * A diferencia de los otros tres, este NO deshabilita el switch: apilar dos reglas puede ser
 * deliberado y el backend no distingue la intención, así que avisa y deja seguir.
 */
const STACKED_RULE_HINT =
  "Hay otra regla activa para esta acción que puede pagar por el mismo hecho. Revisá si no quisiste reemplazar una por la otra.";

/**
 * El switch deshabilitado no recibe el mouse (PrimeReact le pone pointer-events: none a
 * .p-disabled), así que un tooltip en el propio switch nunca aparece: va en este span, que sí lo
 * recibe. Un tooltip por fila: uno compartido con selector de clase no se enganchaba a las filas.
 */
const ActiveSwitchHint = ({ hint, children }: { hint?: string; children: ReactNode }) => {
  const wrapperRef = useRef<HTMLSpanElement>(null);
  return (
    <span ref={wrapperRef} className="inline-flex" data-testid="point-rule-active-switch">
      {children}
      {hint !== undefined && <Tooltip target={wrapperRef} content={hint} position="top" />}
    </span>
  );
};

export const PointRules = () => {
  const scope = useTenantScope();
  const gate = useTenantGate();
  const pointsFlag = useIsFeatureFlagEnabled("gamification_points_enabled");
  const isSuperAdmin = scope.role === "SUPER_ADMIN";
  const tenantId = gate.tenantId;

  const [dialogTenantId, setDialogTenantId] = useState(tenantId);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [selectedRule, setSelectedRule] = useState<PointRule | null>(null);
  const [activatingRule, setActivatingRule] = useState(false);
  const [savingRuleId, setSavingRuleId] = useState<number | null>(null);
  const [ruleToView, setRuleToView] = useState<PointRule>();
  const [ruleToDeactivate, setRuleToDeactivate] = useState<PointRule | null>(null);
  const [filters, setFilters] = useState<PointRuleFilterValues>(EMPTY_POINT_RULE_FILTERS);

  // Cambiar el tenant del header cierra los modales: la regla abierta era del tenant anterior.
  if (dialogTenantId !== tenantId) {
    setDialogTenantId(tenantId);
    setDialogVisible(false);
    setSelectedRule(null);
    setRuleToView(undefined);
    setRuleToDeactivate(null);
  }

  const {
    currentData: rules,
    isLoading,
    isFetching,
    isError,
    error: rulesError,
  } = useGetPointRulesQuery(tenantId ?? "", {
    skip: !gate.allowed || !pointsFlag.enabled || pointsFlag.loading || scope.loading,
  });
  const [updatePointRule] = useUpdatePointRuleMutation();
  const skipEventCatalog = tenantId === null || !gate.allowed || !pointsFlag.enabled;
  const { data: eventTypes } = useGetEventTypesQuery(
    { tenantId: tenantId ?? undefined },
    { skip: skipEventCatalog }
  );
  const { data: eventSubTypes } = useGetEventSubTypesQuery(
    { tenantId: tenantId ?? undefined },
    { skip: skipEventCatalog }
  );

  const visibleRules = useMemo(() => filterPointRules(rules ?? [], filters), [rules, filters]);

  const openCreateDialog = () => {
    setSelectedRule(null);
    setActivatingRule(false);
    setDialogVisible(true);
  };

  const openEditDialog = (rule: PointRule, activating: boolean = false) => {
    setSelectedRule(rule);
    setActivatingRule(activating);
    setDialogVisible(true);
  };

  const canMoveCustomWindow = (rule: PointRule): boolean =>
    canEditPointRuleField(rule, "windowStartsOn") && canEditPointRuleField(rule, "windowEndsOn");

  const closeDialog = () => setDialogVisible(false);

  const handleToggleActive = async (rule: PointRule) => {
    // Un período que ya empezó no se puede prender tal cual: se abre la edición con fechas nuevas.
    if (needsNewCustomWindowToActivate(rule)) {
      openEditDialog(rule, true);
      return;
    }
    setSavingRuleId(rule.id);
    try {
      await updatePointRule({
        id: rule.id,
        tenantId: rule.tenantId,
        changes: buildActiveToggleChanges(rule),
      }).unwrap();
      toast.success("Regla actualizada. Los cambios no son retroactivos.");
    } catch (error) {
      toast.error(getApiErrorMessage(error) ?? "No se pudo actualizar la regla");
    } finally {
      setSavingRuleId((current) => (current === rule.id ? null : current));
    }
  };

  const activeBodyTemplate = (rule: PointRule) => {
    const missingPoints =
      !rule.active && rule.pointsValue === null && rule.ruleKind !== "entity_amount";
    const withoutAccrual = !rule.active && isPointRuleActionWithoutAccrual(rule.actionType);
    // La racha no está en la lista sin acreditación (el motor sí la paga): lo que la frena es
    // que el BO todavía no la lanzó, y ese es el motivo que corresponde mostrar.
    const comingSoon = !rule.active && isPointRuleComingSoon(rule);
    const frozenPastWindow = needsNewCustomWindowToActivate(rule) && !canMoveCustomWindow(rule);
    const blockedHint = withoutAccrual
      ? WITHOUT_ACCRUAL_HINT
      : comingSoon
      ? COMING_SOON_EDIT_HINT
      : missingPoints
      ? POINTS_REQUIRED_HINT
      : frozenPastWindow
      ? PAST_CUSTOM_WINDOW_ACTIVATION_MESSAGE
      : undefined;
    const disabled =
      blockedHint !== undefined ||
      savingRuleId === rule.id ||
      !canEditPointRuleField(rule, "active");
    // El aviso de apilado no entra en `disabled`: sólo informa. Un motivo que sí bloquea tiene
    // prioridad, porque ahí el switch no se puede tocar y explicar por qué es más útil.
    const stackedHint = rule.stackedWithRuleId !== null ? STACKED_RULE_HINT : undefined;

    return (
      <ActiveSwitchHint hint={blockedHint ?? stackedHint}>
        <InputSwitch
          checked={rule.active}
          disabled={disabled}
          aria-label={`${rule.active ? "Desactivar" : "Activar"} regla ${POINT_RULE_ACTION_LABELS[rule.actionType]}`}
          onChange={() => handleToggleActive(rule)}
        />
      </ActiveSwitchHint>
    );
  };

  const actionBodyTemplate = (rule: PointRule) => (
    <span className="inline-flex flex-column align-items-start" style={{ gap: "var(--space-6)" }}>
      {POINT_RULE_ACTION_LABELS[rule.actionType]}
      {(isPointRuleActionUpcoming(rule.actionType) || isPointRuleComingSoon(rule)) && (
        <StatusBadge label="Próximamente" tone="info" />
      )}
    </span>
  );

  const actionsBodyTemplate = (rule: PointRule) => (
    <RowActions
      collapsed
      actions={[
        {
          icon: "pi pi-eye",
          label: "Ver detalle",
          severity: "info",
          onClick: () => setRuleToView(rule),
        },
        {
          icon: "pi pi-pencil",
          label: "Editar regla",
          onClick: () => openEditDialog(rule),
          // Mientras su switch se guarda, la fila todavía no tiene el estado nuevo.
          disabled: savingRuleId === rule.id || isPointRuleComingSoon(rule),
          disabledReason: isPointRuleComingSoon(rule)
            ? COMING_SOON_EDIT_HINT
            : undefined,
        },
        {
          icon: "pi pi-trash",
          label: "Dar de baja",
          severity: "danger",
          onClick: () => setRuleToDeactivate(rule),
          // El BO es más restrictivo que el backend a propósito: el DELETE acepta a un
          // TENANT_ADMIN sobre su propia institución, pero el ALTA de reglas es solo de
          // SUPER_ADMIN. Ofrecerle la baja a quien después no puede volver a crear la regla es
          // una puerta de una sola dirección, y el modal justamente promete lo contrario.
          visible: isSuperAdmin,
          // Una acción que el BO no deja ni configurar tampoco se da de baja desde acá: la
          // racha se administra entera desde su editor, no fila por fila.
          disabled: savingRuleId === rule.id || isPointRuleComingSoon(rule),
          disabledReason: isPointRuleComingSoon(rule)
            ? COMING_SOON_EDIT_HINT
            : undefined,
        },
      ]}
    />
  );

  if (scope.loading) {
    return <PointsPageLoading title={PAGE_TITLE} />;
  }

  if (!gate.allowed) {
    return (
      <PointsPageCard>
        <PageHeader title={PAGE_TITLE} />
        <TenantGateBanner gate={gate} action="ver y editar sus reglas de puntos" />
      </PointsPageCard>
    );
  }

  if (pointsFlag.loading) {
    return <PointsPageLoading title={PAGE_TITLE} />;
  }

  if (!pointsFlag.enabled) {
    return (
      <PointsPageCard>
        <PageHeader title={PAGE_TITLE} />
        <div className="flex justify-content-center p-4">
          <Message severity="info" text="La sección está deshabilitada para este tenant." />
        </div>
      </PointsPageCard>
    );
  }

  return (
    <>
      <PointsPageCard>
        <PageHeader
          title={PAGE_TITLE}
          onCreate={isSuperAdmin ? openCreateDialog : undefined}
          createLabel="Crear regla"
          // El alta valida duplicados contra las reglas del tenant: sin la lista cargada, una
          // regla repetida pasaba y la base la rechazaba con un 500.
          createDisabled={rules === undefined}
          createDisabledReason={
            isError
              ? "No se pudieron cargar las reglas del tenant"
              : "Cargando las reglas del tenant…"
          }
        />

        <BenefitsMetricsRefreshingIndicator visible={isFetching && !isLoading} />

        {!isError && <PointRuleFilters filters={filters} onChange={setFilters} />}

        {/* Con error no se muestra la tabla: "No hay reglas todavía" mentiría sobre un fallo. */}
        {isError ? (
          <Message
            severity="error"
            text={getApiErrorMessage(rulesError) ?? "No se pudieron cargar las reglas."}
          />
        ) : (
          <>
            <DataTable
              style={rowActionsTableStyle("1rem", "5.5rem")}
              value={visibleRules}
              loading={isLoading}
              dataKey="id"
              // Las celdas de Activa y Acciones dependen de savingRuleId, que no es parte de la
              // fila: con el memo por defecto (compara solo rowData) quedaban bloqueadas.
              cellMemo={false}
              stripedRows
              responsiveLayout="scroll"
              emptyMessage={<EmptyState entity="reglas" filtered={hasPointRuleFilters(filters)} />}
            >
              <Column header="Acción" body={actionBodyTemplate} />
              <Column header="Título" body={(rule: PointRule) => rule.displayTitle ?? "—"} />
              <Column
                header="Tipo"
                body={(rule: PointRule) => POINT_RULE_KIND_LABELS[rule.ruleKind]}
              />
              <Column header="Puntos" body={(rule: PointRule) => formatPointRuleValue(rule)} />
              <Column
                header="Tipo de evento"
                body={(rule: PointRule) => formatPointRuleEventScope(rule, eventTypes, eventSubTypes)}
                bodyClassName="white-space-nowrap"
              />
              <Column
                header="Visible"
                body={(rule: PointRule) => (
                  <StatusBadge
                    label={rule.visibleToUser ? "Sí" : "No"}
                    tone={rule.visibleToUser ? "success" : "error"}
                  />
                )}
              />
              <Column
                header="Activa"
                body={activeBodyTemplate}
                alignHeader="center"
                bodyClassName="text-center"
              />
              <Column
                header={<span className="row-actions-label">Acciones</span>}
                headerClassName="row-actions-th"
                bodyClassName="row-actions-cell"
                body={actionsBodyTemplate}
              />
            </DataTable>
          </>
        )}
      </PointsPageCard>

      {tenantId !== null && (
        <PointRuleDetailDialog
          tenantId={tenantId}
          rule={ruleToView}
          rules={rules ?? []}
          onHide={() => setRuleToView(undefined)}
        />
      )}

      {tenantId !== null && (
        <PointRuleFormDialog
          visible={dialogVisible}
          onHide={closeDialog}
          tenantId={tenantId}
          rule={selectedRule}
          rules={rules ?? []}
          activating={activatingRule}
        />
      )}

      <DeactivatePointRuleDialog
        rule={ruleToDeactivate}
        onHide={() => setRuleToDeactivate(null)}
      />
    </>
  );
};
